<script setup lang="ts">
import { type PopoverPlacement, usePopover } from './usePopover';
import { ref, toRef, computed } from 'vue';
import AppPopoverBody from './AppPopoverBody.vue';
import { syncRef, useVModel } from '@vueuse/core';
import type { UiPadding } from '#shared/types/ui';

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    placement?: PopoverPlacement;
    padding?: UiPadding;
    distance?: number;
    withArrow?: boolean;
    sameWidth?: boolean;
    focusOnShow?: boolean;
    closeOnBlur?: boolean;
    transparent?: boolean;
  }>(),
  {
    modelValue: false,
    placement: 'auto',
    padding: 5,
    distance: 0,
    withArrow: true,
    sameWidth: false,
    focusOnShow: false,
    closeOnBlur: true,
    transparent: true,
  }
);
defineEmits<{
  (e: 'update:modelValue', modelValue: boolean): void;
}>();

const popoverRef = ref<InstanceType<typeof AppPopoverBody>>();
const referenceRef = ref<HTMLDivElement>();
const { isVisible, popoverStyles, arrow } = usePopover(
  {
    popover: toRef(() => popoverRef.value?.el),
    reference: referenceRef,
    arrow: toRef(() => popoverRef.value?.arrow),
  },
  computed(() => ({
    placement: props.placement,
    sameWidth: props.sameWidth,
    focusOnShow: props.focusOnShow,
    distance: props.distance,
    isClosableOnBlur: props.closeOnBlur,
  }))
);
const modelValue = useVModel(props, 'modelValue');

syncRef(modelValue, isVisible, {});
</script>

<template>
  <div ref="referenceRef">
    <slot name="reference" />
  </div>
  <AppPopoverBody
    ref="popoverRef"
    v-model="isVisible"
    :style="popoverStyles"
    :padding="props.padding"
    :transparent="props.transparent"
    :arrow-coords="arrow"
  >
    <slot name="popover" />
  </AppPopoverBody>
</template>
